import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, TextField, Button, Grid, Box, Typography, CircularProgress, CssBaseline, Link, Divider, Select, MenuItem } from '@mui/material';
import { signInWithEmailAndPassword, getIdToken, onIdTokenChanged, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from './firebase';
import { useHistory, Link as DomLink } from 'react-router-dom';
import { useAppContext } from './AppContext';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import useAuthManagement from './useAuthManagement';
import { requestFirebaseNotificationPermission } from './firebase';
import { Turnstile } from '@marsidev/react-turnstile';
import { FcGoogle } from 'react-icons/fc';

// Constants
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TURNSTILE_VERIFY_URL = '/api/verify-turnstile';
const LOGO_SRC = '/Order-Master-Logo-SVG.svg';
const LOGO_ALT = 'App Logo';
const LOGO_DIMENSIONS = { width: 350, height: 49 };
const REDIRECT_PATH = '/neworder';
const FORGOT_PASSWORD_PATH = '/forgot-password';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'my', label: 'Burmese', flag: '🇲🇲' },
];

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(true); // New state for authentication check
  const [fcmToken, setFcmToken] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef(null);
  const history = useHistory();
  const { userRole, setUserId, t, language, setLanguage } = useAppContext();
  const { isAuthenticated, saveToken } = useAuthManagement();

  // Check if user is authenticated
  useEffect(() => {
    if (isAuthenticated && userRole !== null) {
      history.push(REDIRECT_PATH);
    } else {
      setAuthChecking(false); // Stop showing the spinner if not authenticated
    }
  }, [isAuthenticated, userRole, history]);

  // Initialize FCM
  useEffect(() => {
    const initializeFCM = async () => {
      try {
        const token = await requestFirebaseNotificationPermission();
        if (token) {
          setFcmToken(token);
        } else {
          console.warn('FCM permission not granted');
        }
      } catch (err) {
        console.error('FCM initialization failed:', err);
      }
    };

    initializeFCM();
  }, []);

  // Set up onIdTokenChanged listener
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          const newIdToken = await getIdToken(user);
          saveToken({ token: newIdToken });
          console.log('ID token refreshed');
        } catch (error) {
          console.error('Error refreshing ID token:', error);
        }
      } else {
        saveToken({ token: null });
        setUserId(null);
        console.log('User signed out');
      }
    });

    return () => unsubscribe();
  }, [saveToken, setUserId]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value.trim() }));
  }, []);

  // Handle Turnstile events
  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileError(false);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileError(true);
    setTurnstileToken('');
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken('');
  }, []);

  // Handle login logic
  const handleLogin = useCallback(async () => {
    const { email, password } = credentials;

    if (!email || !password) {
      setError(t('Please enter both email and password.'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idToken = await getIdToken(user);

      const userDocRef = doc(db, 'users', email);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error('No such user!');
      }

      const userData = userDoc.data();
      setUserId(email);
      saveToken({ token: idToken });

      const shopId = userData.shopId;
      if (shopId && fcmToken) {
        const shopDocRef = doc(db, 'shops', shopId);
        try {
          await updateDoc(shopDocRef, {
            fcmTokens: arrayUnion(fcmToken),
          });
        } catch (error) {
          if (error.code === 'not-found') {
            await setDoc(shopDocRef, { fcmTokens: [fcmToken] });
          } else {
            console.error('Failed to store FCM token:', error);
          }
        }
        console.log(`FCM token stored for shop ${shopId}`);
      } else {
        console.warn('Missing shopId or FCM token');
      }

      // history.push(REDIRECT_PATH); // Removed: let useEffect handle redirect when userRole is set
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || t('Invalid email or password.'));
      setTurnstileToken('');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    } finally {
      setLoading(false);
    }
  }, [credentials, fcmToken, history, saveToken, setUserId, t]);

  // Handle Google login logic
  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await getIdToken(user);

      const userDocRef = doc(db, 'users', user.email);
      const userDoc = await getDoc(userDocRef);

      let userData;
      if (!userDoc.exists()) {
        // New Google user: create a user record with owner role (matching existing schema)
        const derivedName = user.displayName || user.email?.split('@')[0] || '';
        const derivedShopId = derivedName;
        userData = {
          isOnShift: false,
          name: derivedName,
          password: '',
          role: 'owner',
          shopId: derivedShopId,
          username: derivedName,
        };
        await setDoc(userDocRef, userData);

        // Create corresponding shop document
        const shopDocRef = doc(db, 'shops', derivedShopId);
        const shopData = {
          credit: 100, // Free credit for new user
          phoneNumber: '',
          saleTax: '0',
          salesTax: '0',
          shopAddress: '',
          shopLogo: '',
          shopName: derivedName,
          useShiftSystem: true,
        };
        await setDoc(shopDocRef, shopData);
      } else {
        userData = userDoc.data();
      }

      setUserId(user.email);
      saveToken({ token: idToken });

      const shopId = userData.shopId;
      if (shopId && fcmToken) {
        const shopDocRef = doc(db, 'shops', shopId);
        try {
          await updateDoc(shopDocRef, {
            fcmTokens: arrayUnion(fcmToken),
          });
        } catch (error) {
          if (error.code === 'not-found') {
            await setDoc(shopDocRef, { fcmTokens: [fcmToken] });
          } else {
            console.error('Failed to store FCM token:', error);
          }
        }
        console.log(`FCM token stored for shop ${shopId}`);
      } else {
        console.warn('Missing shopId or FCM token');
      }

      // history.push(REDIRECT_PATH); // Removed: let useEffect handle redirect when userRole is set
    } catch (error) {
      console.error('Google login error:', error);
      setError(error.message || t('Google sign-in failed.'));
    } finally {
      setLoading(false);
    }
  }, [fcmToken, history, saveToken, setUserId, t]);

  if (authChecking || (isAuthenticated && userRole === null)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Box sx={{ width: '100%', px: 2, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            size="small"
            renderValue={(val) => {
              const option = LANGUAGE_OPTIONS.find((opt) => opt.value === val);
              return option ? `${option.flag} ${t(option.label)}` : val;
            }}
            sx={{ minWidth: 130 }}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box component="span" sx={{ mr: 1 }}>
                  {option.flag}
                </Box>
                {t(option.label)}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img src={LOGO_SRC} alt={LOGO_ALT} {...LOGO_DIMENSIONS} />
            </Box>
            <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleGoogleLogin}
                disabled={loading}
                startIcon={<FcGoogle />}
                sx={{ py: 1.5 }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : t('Continue with Google')}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider>{t('or')}</Divider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('Email')}
                name="email"
                variant="outlined"
                value={credentials.email}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('Password')}
                name="password"
                type="password"
                variant="outlined"
                value={credentials.password}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="current-password"
              />
            </Grid>
            <Grid item xs={12}>
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                theme="light"
                onSuccess={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
              />
            </Grid>
            {turnstileError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {t('Turnstile verification error. Please try again.')}
                </Typography>
              </Grid>
            )}
            {error && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">{error}</Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleLogin}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('Login')}
              </Button>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Link component={DomLink} to={FORGOT_PASSWORD_PATH} underline="hover">
                {t('Forgot Password?')}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Container>
      </Box>
    );
  }
  // Returning null here avoids rendering blank content if redirect is in progress.
  return null;
};

export default Login;
